import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { Grid, Slide } from '@material-ui/core';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend, Filler,
    ScriptableContext
} from 'chart.js';
import { connect } from "react-redux";
import { ApplicationState } from "../../store";
import * as APIStore from "../../store/APIStore";
import BarChartIcon from '@material-ui/icons/BarChart';
import { getMonthName } from "../../util/DateHelper";

type OwnProps = {
    month: number;
    year: number;
};

const mapState = (state: ApplicationState) => state.vitalsContext;

type StateProps = ReturnType<typeof mapState>;
type DispatchProps = typeof APIStore.actionCreators;
type FacilityPatientsByMonthProps = OwnProps & StateProps & DispatchProps;

const FacilityPatientsByMonth = (props: FacilityPatientsByMonthProps) => {

    let {
        PhysicianAdmitMonthStats,
        userPracticeCoordinatorProfile,
        APIToken,
        requestCurrentUserCareCoordinatorProfile,
        requestAdmitStatsForProviderForMonth,
        month,
        year
    } = props;

    let [tokenState, setTokenState] = useState<APIStore.TokenType>();

    useEffect(() => {
        if (APIToken && !tokenState?.accessToken) {
            setTokenState(APIToken);
        }
    }, [APIToken, tokenState]);

    useEffect(() => {
        if (userPracticeCoordinatorProfile) {
            let { isErrored, notFound, isLoaded } = userPracticeCoordinatorProfile
            if ((!isErrored && !notFound && !isLoaded) && tokenState?.accessToken) {
                requestCurrentUserCareCoordinatorProfile(tokenState.accessToken);

            } else {
                if (isLoaded && tokenState?.accessToken) {
                    let coordinatorProfile = userPracticeCoordinatorProfile.coordinatorProfile;

                    // Update Admit Month Stats
                    if (!PhysicianAdmitMonthStats.isLoaded && !PhysicianAdmitMonthStats.isLoading) {
                        requestAdmitStatsForProviderForMonth(tokenState.accessToken, month, year);
                    }
                }

            }
        }

    }, [tokenState, userPracticeCoordinatorProfile.coordinatorProfile]);


    if (!PhysicianAdmitMonthStats || PhysicianAdmitMonthStats.isLoading || !PhysicianAdmitMonthStats.isLoaded) {
        return (
            <Slide in timeout={1000} direction="right">
                <Grid container>
                    <Grid item md={4}>
                        <BarChartIcon className="animate-flicker" style={{ fontSize: 100 }} color="disabled"></BarChartIcon>
                    </Grid>
                </Grid>
            </Slide>
        )
    }

    if (PhysicianAdmitMonthStats.isErrored) {
        return (<React.Fragment><p>Error Loading</p></React.Fragment>)
    }

    ChartJS.register(
        CategoryScale,
        LinearScale,
        PointElement,
        LineElement,
        Title,
        Tooltip,
        Legend, Filler
    );
    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: "top" as const,
            },
            title: {
                display: true,
                text: `${getMonthName(month)} ${year} Patients`,
            },
        },
    };

    let width: number, height: number, gradient: CanvasGradient;
    function getBlueGradient(ctx: any, chartArea: any) {
        const chartWidth = chartArea.right - chartArea.left;
        const chartHeight = chartArea.bottom - chartArea.top;
        if (!gradient || width !== chartWidth || height !== chartHeight) {
            // Create the gradient because this is either the first render
            // or the size of the chart has changed
            width = chartWidth;
            height = chartHeight;
            gradient = ctx.createLinearGradient(
                0,
                chartArea.bottom,
                0,
                chartArea.top
            );

            gradient.addColorStop(0, "rgba(82, 189, 228, 0.05)");
            gradient.addColorStop(0.5, "rgba(82, 189, 228, 0.1)");
            gradient.addColorStop(1, "rgba(82, 189, 228, 0.2)");
        }

        return gradient;
    }


    const sortedAdmits = [...PhysicianAdmitMonthStats.physicianAdmits].sort(
        (a: APIStore.AdmitSummaryStatistics, b: APIStore.AdmitSummaryStatistics) =>
            new Date(a.year, a.month - 1, a.day).getTime() - new Date(b.year, b.month - 1, b.day).getTime()
    );
    let labels = sortedAdmits.map((val: APIStore.AdmitSummaryStatistics) => `${getMonthName(val.month)} ${val.day}`);
    let values = sortedAdmits.map((val: APIStore.AdmitSummaryStatistics) => val.totalAdmits);


    const data = {
        labels,
        datasets: [
            {
                label: "Admits",
                data: values,
                borderColor: "rgba(82, 189, 228, 1)",
                backgroundColor: function (context: ScriptableContext<'line'>) {
                    const chart = context.chart;
                    const { ctx, chartArea } = chart;

                    if (!chartArea) {
                        // This case happens on initial chart load
                        return;
                    }
                    return getBlueGradient(ctx, chartArea);
                },

                fill: true,
            },
        ],
    };

    return (
        <React.Fragment>
            <Grid item xs={12} direction="column" container>
                <Grid item xs={12}>
                    <Line
                        options={options}
                        data={data}
                    />
                </Grid>

            </Grid>


        </React.Fragment>
    );
};

export default connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
    (state: ApplicationState) => state.vitalsContext,
    APIStore.actionCreators
)(FacilityPatientsByMonth as any);
